import React, {useContext, useState} from 'react';
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Input from "@amzn/meridian/input";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FormattedMessage, injectIntl} from "react-intl";
import Column from "@meridian/components/column";
import Text from "@amzn/meridian/text";
import Alert from "@amzn/meridian/alert"
import Select, {SelectOption} from "@amzn/meridian/select";
import {CONFIG_TYPE, VAPR_LOCATION_TYPES, VAPR_ORIENTATIONS} from "../../../util/Constants";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {getLocalizedMessage} from "../../../util/Localization";
import {isValidVaprLocationLabel, validateVaprLocationConfig} from "../../../util/Validations";
import VaprOrientationGuide from "../VaprOrientationGuide";

// Add/Update modal for one VAPR location entry (Location Type, Location label, Orientation).
// Staged in the shared draft keyed by `label`; typing an existing label switches to edit mode.
const AddVaprLocationConfigurationModal = (props) => {
    const {mac, intl} = props;
    const {getDraftData, addInstance, editInstance} = useContext(DraftContext)

    const {open, setOpen} = props
    const [show, setShow] = useState(false)
    const [edit, setEdit] = useState(false)

    const [label, setLabel] = useState("")
    const [locationType, setLocationType] = useState("")
    const [orientation, setOrientation] = useState("")

    const [labelErr, setLabelErr] = useState(false)
    const [locationTypeErr, setLocationTypeErr] = useState(false)
    const [orientationErr, setOrientationErr] = useState(false)

    const modalLocationConfig = {
        label: label,
        locationType: locationType,
        orientation: orientation
    }

    const locationConfigMap = {}
    getDraftData({stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION, subKey: mac}).forEach(value => {
        locationConfigMap[value.label] = value;
    });

    // Labels are operator-supplied and can collide with Object.prototype members (e.g. a station
    // named "hasOwnProperty" would shadow the method), so never call it off the map itself.
    const hasStoredConfig = (key) => Object.prototype.hasOwnProperty.call(locationConfigMap, key);

    const isValidLocationTypeValue = (value) =>
        Object.values(VAPR_LOCATION_TYPES).some(type => type.value === value);
    const isValidOrientationValue = (value) =>
        Object.values(VAPR_ORIENTATIONS).some(o => o.value === value);

    const checkValidations = () => {
        return validateVaprLocationConfig(modalLocationConfig);
    }

    const onCloseModal = () => {
        setShow(false);
        setOpen(false)
        resetInputState();
    }

    const onClickConfirm = (modalLocationConfig, mac) => {
        if (checkValidations()) {
            setConfigData(modalLocationConfig, mac);
            setOpen(false)
            resetInputState();
            setShow(false);
        } else {
            setShow(true)
        }
    }

    function resetInputState() {
        setLabel("");
        setLabelErr(false);
        setLocationType("");
        setLocationTypeErr(false);
        setOrientation("");
        setOrientationErr(false);
        setEdit(false);
    }

    const prefill = () => {
        if (hasStoredConfig(label)) {
            const storedConfig = locationConfigMap[label];
            setEdit(true);
            setLocationType(isValidLocationTypeValue(storedConfig.locationType) ? storedConfig.locationType : "");
            setOrientation(isValidOrientationValue(storedConfig.orientation) ? storedConfig.orientation : "");
            setLocationTypeErr(false);
            setOrientationErr(false);
            setLabelErr(!isValidVaprLocationLabel(label));
        } else if (edit) {
            setEdit(false);
            setLocationType("");
            setOrientation("");
        }
    }

    const setConfigData = (modalLocationConfig, mac) => {
        // Decide add vs edit from the label itself, not the blur-driven `edit` flag: confirming an
        // existing label without blurring (e.g. Enter) must still update, never add a duplicate key.
        if (hasStoredConfig(modalLocationConfig.label)) {
            editInstance({
                stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
                subKey: mac,
                instance: modalLocationConfig,
                keyComparator: (config1, config2) => (
                    config1.label === config2.label
                ),
                valueComparator: (config1, config2) => (
                    config1.locationType === config2.locationType &&
                    config1.orientation === config2.orientation
                )
            })
        } else {
            addInstance({
                stateKey: CONFIG_TYPE.VAPR_LOCATION_CONFIGURATION,
                subKey: mac,
                instance: modalLocationConfig
            })
        }
    }

    return (
        <Row>
            <Modal
                title={getLocalizedMessage({intl, stringId: "vaprlocationconfig.addupdate"})}
                open={open}
                onClose={onCloseModal}
                scrollContainer="viewport"
                closeLabel="Close"
                bodySpacingInset="medium">
                <Column spacing={"medium"} spacingInset={"medium"}>
                    <Column spacing="200">
                        <b> <FormattedMessage id="modal.addlocationheading"
                                              defaultMessage="To edit an existing configuration, enter the Location Id."/>
                        </b>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="vapr-location-type-input">
                            {getLocalizedMessage({intl, stringId: "location.type"})}
                        </Text>
                        <Select id="vapr-location-type-input"
                            value={locationType}
                            onChange={(value) => {
                                setLocationType(value);
                                setLocationTypeErr(false);
                            }}
                            onBlur={() => setLocationTypeErr(locationType === "")}
                            placeholder={<FormattedMessage id="modal.enter_value" defaultMessage="Enter value..."/>}
                            width="100%"
                            error={locationTypeErr}>
                            {Object.values(VAPR_LOCATION_TYPES).map(({label: typeLabel, value}, index) =>
                                <SelectOption key={index} value={value} label={typeLabel} />
                            )}
                        </Select>
                    </Column>
                    <Column spacing="200">
                        <Row spacing="100" alignmentVertical="center" wrap="none">
                            <Text tag="label" type="b200" color="primary" htmlFor="vapr-orientation-input">
                                {getLocalizedMessage({intl, stringId: "location.orientation"})}
                            </Text>
                            <VaprOrientationGuide/>
                        </Row>
                        <Select id="vapr-orientation-input"
                            value={orientation}
                            onChange={(value) => {
                                setOrientation(value);
                                setOrientationErr(false);
                            }}
                            onBlur={() => setOrientationErr(orientation === "")}
                            placeholder={<FormattedMessage id="modal.enter_value" defaultMessage="Enter value..."/>}
                            width="100%"
                            error={orientationErr}>
                            {Object.values(VAPR_ORIENTATIONS).map(({label: orientationLabel, value}, index) =>
                                <SelectOption key={index} value={value} label={orientationLabel} />
                            )}
                        </Select>
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary" htmlFor="vapr-location-id-input">
                            {getLocalizedMessage({intl, stringId: "location.locationId"})}
                        </Text>
                        <Input id="vapr-location-id-input"
                               value={label}
                               onChange={setLabel}
                               onBlur={() => {
                                   setLabelErr(label.trim() !== "" && !isValidVaprLocationLabel(label))
                                   prefill();
                               }}
                               error={labelErr}/>
                        {labelErr &&
                            <Text type="b100" color="error">
                                <FormattedMessage id="location.invalidLocationId"
                                                  defaultMessage="Invalid Location ID format"/>
                            </Text>}
                    </Column>
                </Column>
                <ModalFooter>
                    <Column>
                        {!checkValidations() &&
                            show &&
                            <Alert type="warning" size="small">
                                <FormattedMessage id="alert.missingfields"
                                                  defaultMessage="Please enter all fields in correct format!"/>
                            </Alert>}
                    </Column>
                    <Row alignmentHorizontal="right" widths="fit">
                        <Button type="secondary" size="medium" onClick={onCloseModal}>
                            <FormattedMessage id="modal.cancel" defaultMessage="Cancel"/>
                        </Button>
                        <Button type="primary" size="medium"
                                onClick={() => onClickConfirm(modalLocationConfig, mac)}>
                            { hasStoredConfig(label)
                                ? <FormattedMessage id="modal.confirmUpdate" defaultMessage="Confirm update"/>
                                : <FormattedMessage id="modal.confirmAdd" defaultMessage="Confirm add"/>}
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </Row>
    )
}

export default injectIntl(AddVaprLocationConfigurationModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/modal/AddVaprLocationConfigurationModal.js